import { Injectable } from "@angular/core";
import { QuestionConfig, QuestionTypeEnum, QuestionGroup, QuestionConfigTypeEnum, QuestionFormulaType, RelatedQuestion } from "../interfaces/process-config.interface";
import { Goal } from "src/app/goals/_interfaces/goal.interface";
import { ManagerEvaluationRecord } from "src/app/manager-evaluation/_interfaces/manager-evaluation-record";
import { SelfEvaluationRecord } from "src/app/self-evaluation/_interfaces/self-evaluation-record.interface";
import { GenericProcessRecord } from "src/app/generic-process/generic-process-record.interface";
import { ProcessPreview, ProcessTypeEnum } from "../interfaces/process.interface";
import { FinalFeedback } from "src/app/final-feedback/interfaces/final-feedback.interface";
import { ConfirmModalComponent } from "src/app/shared/modals/confirm-modal/confirm-modal.component";
import { ModalController } from "@ionic/angular";
import { TranslationService } from "src/app/shared/services/translation.service";
import { isEmpty, isNil } from "lodash";

@Injectable()
export class UtilService {
    constructor(
        private _modalController: ModalController,
        private _translationService: TranslationService,
    ) { }
    public orderByProperty(array: Array<any>, property: string): Array<any> {
        return array.sort((a, b) => {
            if (a[property] < b[property]) return -1;
            if (a[property] > b[property]) return 1;
            return 0;
        });
    }

    public getDatesDiffInDays(dateOne: string | Date, dateTwo: string | Date): number {
        dateOne = new Date(dateOne);
        dateOne.setHours(0, 0, 0, 0);
        dateTwo = new Date(dateTwo);
        dateTwo.setHours(0, 0, 0, 0);
        const diffTime = Math.abs(dateTwo.getTime() - dateOne.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    public addGoalsAsQuestions(goals: Array<Goal>, groups: Array<QuestionGroup>): Array<QuestionGroup> {
        let indexesToDelete = null;
        groups.forEach((group, gIndex) => {
            group.questions.sort((a, b) => a.order - b.order);

            group.questions.forEach((question, index) => {
                if (question.type === QuestionConfigTypeEnum.Goals) {
                    indexesToDelete = [gIndex, index];
                }
            });
        });

        if (indexesToDelete) {
            const groupIndex = indexesToDelete[0];
            const questionIndex = indexesToDelete[1];

            const group = groups[groupIndex];
            const goalsQuestions = [];

            goals.forEach((goal) => {
                goalsQuestions.push({
                    globalizationConfig: group.questions[questionIndex].globalizationConfig,
                    typeConfig: { ...group.questions[questionIndex].typeConfig },
                    type: group.questions[questionIndex].type,
                    currentIndex: group.questions[questionIndex].currentIndex,
                    goal: goal,
                });
            });

            // Remove a Question original do tipo Goals do formulário
            group.questions.splice(indexesToDelete[1], 1);

            // Adiciona as novas X questions, referentes a cada meta, na posição da Question original do tipo Goals
            group.questions.splice(indexesToDelete[1], 0, ...goalsQuestions);
        }

        return groups;
    }

    public groupQuestions(questions: Array<QuestionConfig>): Array<QuestionConfig> {
        const hasQuestionsSameTypeConfig = this.hasDuplicateProperties(questions, "typeConfig.id") || questions.length == 1;
        if (hasQuestionsSameTypeConfig) {
            const groupedArray: Array<QuestionConfig> = [];

            questions.forEach((question) => {
                if (!question.hideQuestion) {
                    if (question.typeConfig.type === QuestionTypeEnum.Slider) {
                        const typeConfigIndex = groupedArray.findIndex((q) => q.typeConfig.id === question.typeConfig.id);
                        if (typeConfigIndex === -1) {
                            question.typeConfig.type = QuestionTypeEnum.GroupedSlider;
                            question.groupedGlobalizationConfig = [question.globalizationConfig];
                            question.groupedIndexes = [question.currentIndex];
                            question.groupedTypes = [question.type];
                            question.groupedGoals = [question.goal];
                            question.groupedValues = [question.values];
                            question.groupedReportColumns = [question.reportColumn];
                            question.groupedViewType = [question.viewType];
                            groupedArray.push(question);
                        } else {
                            groupedArray[typeConfigIndex].groupedGlobalizationConfig.push(question.globalizationConfig);
                            groupedArray[typeConfigIndex].groupedIndexes.push(question.currentIndex);
                            groupedArray[typeConfigIndex].groupedTypes.push(question.type);
                            groupedArray[typeConfigIndex].groupedGoals.push(question.goal);
                            groupedArray[typeConfigIndex].groupedValues.push(question.values);
                            groupedArray[typeConfigIndex].groupedReportColumns.push(question.reportColumn);
                            groupedArray[typeConfigIndex].groupedViewType.push(question.viewType);
                        }
                    } else {
                        groupedArray.push(question);
                    }
                } else {
                    if (question.typeConfig.type === QuestionTypeEnum.Slider) {
                        const typeConfigIndex = groupedArray.findIndex((q) => q.typeConfig.id === question.typeConfig.id);
                        if (typeConfigIndex != -1) groupedArray[typeConfigIndex].hasQuestionAttributes = true;
                    }
                }
            });

            return groupedArray;
        }

        return questions;
    }

    public adjustRatingsForGoals(
        groups: Array<QuestionGroup>,
        evaluation: SelfEvaluationRecord | ManagerEvaluationRecord | GenericProcessRecord | FinalFeedback,
    ): SelfEvaluationRecord | ManagerEvaluationRecord | GenericProcessRecord | FinalFeedback {
        const goalsIndexes = this.getGoalsFieldsIndexes(groups);
        const groupedValues = [];

        // Para cada meta avaliada, adicionar em um array um objeto contendo o id da meta e a nota atribuída
        goalsIndexes.forEach((goalIndex) => {
            const value = evaluation["field" + goalIndex.index];
            if (value) {
                groupedValues.push({
                    id: goalIndex.id,
                    value: value,
                });
            }
        });

        if (groupedValues.length > 0) {
            // Tornar esse array em uma string, que será enviado como resposta de um 'field' só para o servidor
            const stringValue = JSON.stringify(groupedValues);
            evaluation["field" + goalsIndexes[0].index] = stringValue;
        }

        // Ajusta a posição dos demais 'fields'
        const keys = Object.keys(evaluation);
        keys.forEach((key) => {
            if (key.includes("field")) {
                const keyField = key.split("field");
                const keyIndex = parseInt(keyField[1], 10);

                if (goalsIndexes.length > 0) {
                    const lastGoalIndex = goalsIndexes[goalsIndexes.length - 1].index;

                    if (keyIndex > lastGoalIndex) {
                        evaluation["field" + (keyIndex - (goalsIndexes.length - 1))] = evaluation["field" + keyIndex];
                    }
                }
            }
        });

        // Remove os 'fields' que eram das questions de cada meta
        keys.forEach((key) => {
            if (key.includes("field")) {
                const keyField = key.split("field");
                const keyIndex = parseInt(keyField[1], 10);

                if (keyIndex >= keys.length - (goalsIndexes.length - 1)) delete evaluation["field" + keyIndex];
            }
        });

        return evaluation;
    }

    public getGoalsFieldsIndexes(groups: Array<QuestionGroup>): Array<{ id: number; index: number }> {
        const goalsIndexes = [];
        let currentIndex = 1;

        groups.forEach((group) => {
            group.questions.forEach((question) => {
                if (question.groupedGoals) {
                    question.groupedGoals.forEach((goal) => {
                        if (!!goal) {
                            goalsIndexes.push({
                                id: goal.id,
                                index: question.groupedIndexes ? question.groupedIndexes[question.groupedGoals.indexOf(goal)] : currentIndex,
                            });
                        }
                        currentIndex++;
                    });
                } else if (question.goal) {
                    goalsIndexes.push({
                        id: question.goal.id,
                        index: question.groupedIndexes ? question.groupedIndexes[question.groupedGoals.indexOf(question.goal)] : currentIndex,
                    });
                    currentIndex++;
                } else {
                    currentIndex++;
                }
            });
        });

        return goalsIndexes;
    }

    public getEvaluationValue(
        questionConfig: QuestionConfig,
        editingEvaluation: SelfEvaluationRecord | ManagerEvaluationRecord | FinalFeedback,
        goalsIndexes,
        currentIndex: number,
    ): string {
        if (!editingEvaluation) return "";

        const firstIndex = goalsIndexes.length > 0 ? goalsIndexes[0].index : -1;
        const lastIndex = goalsIndexes.length > 0 ? goalsIndexes[goalsIndexes.length - 1].index : -1;

        const isFieldAfterGoals = lastIndex < 0 ? false : currentIndex > lastIndex;
        const isGoalField = currentIndex >= firstIndex && currentIndex <= lastIndex;

        if (isFieldAfterGoals) {
            return editingEvaluation["field" + (currentIndex - (goalsIndexes.length - 1))];
        } else if (isGoalField) {
            const goalsValues = editingEvaluation["field" + firstIndex];
            if (goalsValues) {
                const parsedValues = JSON.parse(goalsValues);
                const currentValue = parsedValues[currentIndex - firstIndex];
                return currentValue ? currentValue.value : "";
            }
            return "";
        }

        if (questionConfig.typeConfig.possibleValues && questionConfig.typeConfig.possibleValues.length > 0) {
            if (!questionConfig.typeConfig.possibleValues.some((x) => x === editingEvaluation["field" + currentIndex])) return "";
        }

        return editingEvaluation ? editingEvaluation["field" + currentIndex] : "";
    }

    public hasDuplicateProperties(array: Array<any>, property: string): boolean {
        const mappedArray = array.map((item) => item[property]);
        return mappedArray.some((item, id) => {
            return mappedArray.indexOf(item) !== id;
        });
    }

    public insertProcessIdInLocalStorage(processId: number): void {
        localStorage.setItem("ProcessId", processId !== null ? processId.toString() : null);
    }

    public removeProcessIdFromLocalStorage(): void {
        localStorage.removeItem("ProcessId");
    }

    public async calculateRelatedQuestions(form, questions: QuestionConfig[], previousform = null, goals: Array<{ id: number; index: number }> = []): Promise<{ field: string; value: number }[]> {
        if (!(questions && questions.length)) return null;
        const getQuestionOrder = (questionId: number) => {
            const foundQuestion = questions.find((question) => question.id === questionId);
            if (foundQuestion) return foundQuestion.order ? foundQuestion.order : foundQuestion.currentIndex;
            throw new Error("Question order not found");
        };

        const hasAllAnswersNA = (questions: RelatedQuestion[]) => {
            return questions && questions.length >= 1 && questions.every((q) => parseFloat(form["field" + getQuestionOrder(q.questionId)]) == -1);
        };

        if (questions.filter((question) => question.relatedQuestions.length).some((q) => hasAllAnswersNA(q.relatedQuestions))) {
            const modalConfirm = await this._modalController.create({
                component: ConfirmModalComponent,
                cssClass: "confirm-calculate-modal",
                componentProps: {
                    title: this._translationService.getTranslation("generic.warning"),
                    message: this._translationService.getTranslation("calculate.warning"),
                    btnConfirmText: this._translationService.getTranslation("generic.confirm"),
                },
            });

            await modalConfirm.present();

            const { data } = await modalConfirm.onDidDismiss();

            if (!data && previousform) {
                const changedControl = Object.keys(form).find((key) => previousform[key] !== form[key]);
                form[changedControl] = previousform[changedControl];
                return [
                    {
                        field: changedControl,
                        value: previousform[changedControl],
                    },
                ];
            }
        }

        const calculateSumOfRelatedQuestions = (question: QuestionConfig) => {
            return question.relatedQuestions.reduce((acc, curr) => {
                const questionOrder = getQuestionOrder(curr.questionId);
                let value = parseFloat(form["field" + questionOrder]);
                if (!isNaN(value) && value >= 0) return (acc += parseFloat(form["field" + questionOrder]) || 0);
                return acc;
            }, 0);
        };

        const calculateAverageOfRelatedQuestions = (question: QuestionConfig) => {
            const sum = calculateSumOfRelatedQuestions(question);

            if (hasAllAnswersNA(question.relatedQuestions)) return NaN;
            let validRelatedQuestions = question.relatedQuestions.filter((rQuestion) => {
                let order = getQuestionOrder(rQuestion.questionId);
                let value = parseFloat(form["field" + order]);
                return !isNaN(value);
            });

            validRelatedQuestions = validRelatedQuestions.filter((rQuestion) => {
                let order = getQuestionOrder(rQuestion.questionId);

                if (rQuestion?.question?.type === QuestionConfigTypeEnum.Goals) {
                    let sum = 0
                    goals.forEach((goal) => {
                        sum += parseFloat(form["field" + goal.index]);
                    });
                    return (sum / goals.length) >= 0;
                }

                let goalQuestion = validRelatedQuestions.find(rq => rq.question.type === QuestionConfigTypeEnum.Goals)
                if (goalQuestion?.question?.order < order) {
                    order += (goals.length - 1);
                }

                let value = parseFloat(form["field" + order]);
                return value >= 0;
            });

            return question.relatedQuestions.length > 0 && validRelatedQuestions.length > 0 ? sum / validRelatedQuestions.length : 0;
        };

        const calculationFunctions = {
            [QuestionFormulaType.Average]: calculateAverageOfRelatedQuestions,
            [QuestionFormulaType.Sum]: calculateSumOfRelatedQuestions,
        };

        return questions
            .filter((question) => question.relatedQuestions.length)
            .map((question) => {
                const calculationFunction = calculationFunctions[question.formula];
                const resultValue = calculationFunction ? calculationFunction(question) : 0;
                let questionOrder = question.order;
                let goalsQuestion = questions.find(q => q.type === QuestionConfigTypeEnum.Goals);
                if (goalsQuestion) {
                    if (goalsQuestion.order < question.order) {
                        let goalsCount = goals.length;
                        questionOrder = (questionOrder + goalsCount - 1);
                    }
                }
                const fieldName = "field" + questionOrder;
                form[fieldName] = resultValue;
                return {
                    field: fieldName,
                    value: resultValue,
                };
            });
    }

    public setUrlStateGuard(processType: ProcessTypeEnum, processes: ProcessPreview[] = null, processId: number = null) {
        return {
            accessByComponent: true,
            processId: processes !== null ? processes.find((p) => p.processType === processType).processId : processId,
        };
    }

    public convertDateString(dateString, useTimeZone: boolean = false, onlyMonthDay: boolean = false): string {
        let language = this._translationService.getSelectedLanguage();

        if (useTimeZone) {
            const date = new Date(dateString);

            if (isNaN(date.getTime())) {
                return dateString;
            }

            if (language === 'en-US') {
                return date.toLocaleString('en-US');
            } else {
                return date.toLocaleString('pt-BR');
            }
        }
        let day = null;
        let month = null;
        let yearAndTime = null;
        if (!isNil(dateString) && !isEmpty(dateString)) {
            if (dateString.includes("/")) {
                [day, month, yearAndTime] = dateString.split("/");
            }
            if (dateString.includes("-")) {
                let dayAndTime = null;
                [yearAndTime, month, dayAndTime] = dateString.split("-");
                let dayAndTimeSplit = dayAndTime.includes(",")
                    ? dayAndTime.split(",")
                    : (dayAndTime.includes("T")
                        ? dayAndTime.split("T")
                        : dayAndTime
                    );
                yearAndTime = yearAndTime + "," + (!isNil(dayAndTimeSplit) ? dayAndTimeSplit[1] : "");
                day = (!isNil(dayAndTimeSplit) ? dayAndTimeSplit[0] : day);
            }
        }

        const commaRegex = /,\s*/;
        const spaceRegex = /\s+/;

        let yearAndTimeArray: string[];

        if (yearAndTime) {
            yearAndTimeArray = yearAndTime.match(commaRegex) ?
                yearAndTime.split(commaRegex).map(str => str.trim()) :
                yearAndTime.split(spaceRegex).map(str => str.trim());
        } else {
            yearAndTimeArray = [];
        }

        let [year, time] = yearAndTimeArray;
        if (time && (time.includes("AM") || time.includes("PM"))) {
            if (time.includes("PM")) {
                const universalHour = time.replace(' PM', '');
                const [hours, minutes, seconds] = universalHour.split(':').map(Number);

                let newHour = hours !== 12 ? (hours + 12) : hours;
                if (newHour >= 24) {
                    newHour -= 24;
                }
                const stringHour = (newHour < 10 ? '0' : '') + newHour;
                const stringMinutes = (minutes < 10 ? '0' : '') + minutes;
                const stringSeconds = (seconds < 10 ? '0' : '') + seconds;

                time = `${stringHour}:${stringMinutes}:${stringSeconds}`;
            }
            if (time.includes("AM")) {
                const universalHour = time.replace(' AM', '');
                const [hours, minutes, seconds] = universalHour.split(':').map(Number);

                let newHour = hours + 12;
                if (newHour >= 24) {
                    newHour -= 24;
                }

                const stringHour = (newHour < 10 ? '0' : '') + newHour;
                const stringMinutes = (minutes < 10 ? '0' : '') + minutes;
                const stringSeconds = (seconds < 10 ? '0' : '') + seconds;

                time = `${stringHour}:${stringMinutes}:${stringSeconds}`;
            }
            const timeParts = time.split(" ");
            if (timeParts.length === 2) {
                time = timeParts[0].trim();
            }
        }

        if (time && time !== undefined && time.includes("."))
            time = time.split(".")[0];

        if (language === 'en-US') {
            return onlyMonthDay ? `${month}/${day}` : `${month}/${day}/${year} ${time}`;
        }
        return onlyMonthDay ? `${day}/${month}` : `${day}/${month}/${year} ${time}`;
    }
}
